import React, { useEffect, useState, useCallback } from "react";
import { adminApi, TOKEN_KEY } from "./adminApi";
import { Users, RefreshCw, TrendingUp, Globe } from "lucide-react";

// Render a flag as an image (works on every OS, unlike emoji flags on Windows).
const Flag = ({ code, country }) => {
  const [failed, setFailed] = useState(false);
  const cc = (code || "").toLowerCase();
  if (failed || !cc || cc === "xx" || cc.length !== 2) {
    return <Globe className="w-5 h-4 text-gray-400" />;
  }
  return (
    <img
      src={`https://flagcdn.com/24x18/${cc}.png`}
      srcSet={`https://flagcdn.com/48x36/${cc}.png 2x`}
      width={24}
      height={18}
      alt={country || code}
      className="rounded-[2px] shadow-sm shrink-0"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

const HeroStat = ({ label, value, sub, icon: Icon, accent, testid }) => (
  <div
    className="relative overflow-hidden bg-white border border-gray-200 rounded-2xl p-8"
    data-testid={testid}
  >
    <div
      className="absolute -right-10 -top-10 w-40 h-40 rounded-full opacity-[0.07]"
      style={{ backgroundColor: accent }}
    />
    <div className="relative flex items-start justify-between">
      <div>
        <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500">
          {label}
        </span>
        <div className="mt-4 text-6xl font-light text-gray-900 tracking-tight tabular-nums leading-none">
          {(value ?? 0).toLocaleString()}
        </div>
        <p className="text-xs text-gray-400 font-light mt-3">{sub}</p>
      </div>
      <span
        className="inline-flex items-center justify-center w-11 h-11 rounded-xl"
        style={{ backgroundColor: `${accent}18`, color: accent }}
      >
        <Icon className="w-5 h-5" />
      </span>
    </div>
  </div>
);

const CountryBreakdown = ({ countries }) => {
  const total = (countries || []).reduce((a, c) => a + c.count, 0);
  const max = Math.max(...(countries || []).map((c) => c.count), 1);

  return (
    <div
      className="bg-white border border-gray-200 rounded-2xl p-8"
      data-testid="visitors-countries"
    >
      <div className="flex items-center gap-2 mb-6">
        <Globe className="w-4 h-4 text-gray-400" />
        <h3 className="text-sm font-medium text-gray-900 tracking-tight">
          Visitors by country
        </h3>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.2em] text-gray-400">
          {(countries || []).length} {countries?.length === 1 ? "country" : "countries"}
        </span>
      </div>

      {!countries || countries.length === 0 ? (
        <div className="text-center py-10">
          <Globe className="w-8 h-8 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-400 font-light">
            No location data yet. Countries appear as real visitors arrive.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {countries.map((c, i) => {
            const pct = total ? Math.round((c.count / total) * 100) : 0;
            return (
              <div key={`${c.code}-${i}`} data-testid={`country-row-${c.code}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Flag code={c.code} country={c.country} />
                    <span className="text-sm text-gray-800 truncate">{c.country}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-medium text-gray-900 tabular-nums">
                      {c.count.toLocaleString()}
                    </span>
                    <span className="text-[10px] font-mono text-gray-400 w-9 text-right">
                      {pct}%
                    </span>
                  </div>
                </div>
                <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#00BCD4] rounded-full transition-all duration-500"
                    style={{ width: `${Math.max((c.count / max) * 100, 4)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const VisitorsPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/analytics/visitors");
      setStats(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        window.location.reload();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div className="space-y-6" data-testid="admin-visitors-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-light text-gray-900 tracking-tight">
            Website Visitors
          </h2>
          <p className="text-sm text-gray-500 font-light mt-1">
            Unique visitors to the public site, counted once per browser.
          </p>
        </div>
        <button
          onClick={fetchStats}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-gray-200 rounded-md hover:border-gray-900 transition-colors"
          data-testid="visitors-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <HeroStat
          label="Total visitors"
          value={stats?.total_unique}
          sub="Distinct visitors since launch"
          icon={Users}
          accent="#00BCD4"
          testid="visitors-total-unique"
        />
        <HeroStat
          label="New this week"
          value={stats?.new_last_7_days}
          sub="First-time visitors · last 7 days"
          icon={TrendingUp}
          accent="#6366F1"
          testid="visitors-new-week"
        />
      </div>

      <CountryBreakdown countries={stats?.countries} />
    </div>
  );
};

export default VisitorsPanel;
